import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// CoworkersPanel.tsx -- floating panel listing the project's saved
// AI co-worker personas. Opened from the + menu's "Agent Co-Workers"
// item. Clicking a coworker arms useAgent.activeCoworker and opens
// the chat window so the user can start a fresh session with that
// persona.
//
// The panel + edit modal both live in this file because they share
// the same domain types and the modal is only ever opened from the
// panel. If they grow we can split.
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../utils/api.js';
import { useChatStore } from '../../hooks/useAgent.js';
import { useAuth } from '../../hooks/useAuth.js';
import { AvatarPicker } from '../AvatarPicker.js';
export function CoworkersPanel({ projectId, open, onClose }) {
    const [coworkers, setCoworkers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [editing, setEditing] = useState(null);
    // Re-fetch on open. Cheap; the list is small.
    const refresh = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const r = await api.get(`/p/${projectId}/coworkers`);
            setCoworkers(r.coworkers ?? []);
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to load co-workers');
        }
        finally {
            setLoading(false);
        }
    }, [projectId]);
    useEffect(() => {
        if (open)
            void refresh();
    }, [open, refresh]);
    const startChatWith = useCallback((cw) => {
        // Per user direction (May 2026): clicking an agent co-worker
        // ALWAYS opens a NEW chat window, even when one with this
        // co-worker is already open. The mental model: a co-worker is
        // a specialist persona that you might bring into MULTIPLE
        // simultaneous topics. The "+ agent co-workers" picker is the
        // entry point to start a NEW topic with that specialist; if the
        // user wanted to resume an existing topic they'd reopen it from
        // the Conversations panel or the per-recipient history popover.
        // Calling openWindow directly (NOT openOrFocusRecipientWindow)
        // skips the de-dupe + auto-resume branches that previously
        // raised an existing window instead of spawning a fresh draft.
        useChatStore.getState().openWindow({
            recipient: {
                kind: 'coworker',
                coworkerId: cw.coworkerId,
                name: cw.name,
                ...(cw.avatarEmoji ? { avatarEmoji: cw.avatarEmoji } : {}),
            },
        });
        onClose();
    }, [onClose]);
    const archiveCoworker = useCallback(async (cw) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm(`Archive "${cw.name}"? Existing chats with them stay accessible; they just disappear from the panel.`))
            return;
        try {
            await api.delete(`/p/${projectId}/coworkers/${cw.coworkerId}`);
            await refresh();
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'failed to archive');
        }
    }, [projectId, refresh]);
    if (!open)
        return null;
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal-bg", onClick: onClose, children: _jsxs("div", { className: "coworkers-panel", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "coworkers-header", children: [_jsx("h3", { children: "Agent Co-Workers" }), _jsx("button", { type: "button", className: "ghost", onClick: onClose, title: "Close", children: '\u2715' })] }), _jsxs("div", { className: "coworkers-body", children: [loading && _jsx("div", { className: "coworkers-empty", children: 'Loading\u2026' }), err && _jsx("div", { className: "error", children: err }), !loading && !err && coworkers.length === 0 && (_jsxs("div", { className: "coworkers-empty", children: [_jsx("p", { children: "No co-workers yet." }), _jsx("p", { style: { color: 'var(--fg-muted)', fontSize: 13 }, children: "Create a persona below to chat with a saved AI specialist whenever you need their expertise." })] })), !loading && coworkers.map((cw) => (_jsxs("div", { className: "coworkers-row", children: [_jsxs("button", { type: "button", className: "coworkers-row-main", onClick: () => startChatWith(cw), title: `Start a new chat with ${cw.name}`, children: [_jsx("span", { className: "coworkers-avatar", "aria-hidden": true, children: cw.avatarEmoji ?? '\uD83E\uDD16' }), _jsxs("div", { className: "coworkers-row-text", children: [_jsx("div", { className: "coworkers-row-name", children: cw.name }), _jsx("div", { className: "coworkers-row-skill", children: (cw.skill || '').split('\n')[0] })] })] }), _jsxs("div", { className: "coworkers-row-actions", children: [_jsx("button", { type: "button", className: "ghost", onClick: () => setEditing(cw), title: "Edit", children: '\u270F\uFE0F' }), _jsx("button", { type: "button", className: "ghost", onClick: () => void archiveCoworker(cw), title: "Archive", children: '\uD83D\uDDD1\uFE0F' })] })] }, cw.coworkerId)))] }), _jsx("div", { className: "coworkers-footer", children: _jsx("button", { type: "button", onClick: () => setEditing('new'), children: "+ New co-worker" }) })] }) }), editing && (_jsx(CoworkerEditModal, { projectId: projectId, existing: editing === 'new' ? null : editing, onClose: () => setEditing(null), onSaved: async () => {
                    setEditing(null);
                    await refresh();
                } }))] }));
}
function CoworkerEditModal({ projectId, existing, onClose, onSaved }) {
    const { user } = useAuth();
    const [name, setName] = useState(existing?.name ?? '');
    const [skill, setSkill] = useState(existing?.skill ?? '');
    const [personality, setPersonality] = useState(existing?.personality ?? '');
    const [avatarEmoji, setAvatarEmoji] = useState(existing?.avatarEmoji ?? '\uD83E\uDD16');
    // Personal toggle (workbench I19). Coworker is "personal" when its
    // participants array exists and contains entries -- typically just
    // [creatorId]. Default for new coworkers is OFF (project-wide).
    // Editing an existing coworker: derive from current participants.
    const [personal, setPersonal] = useState(existing ? !!(existing.participants && existing.participants.length > 0) : false);
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState(null);
    // Keep one undo for the AI buttons so the user can revert if the
    // generated text is worse than what they had typed.
    const [skillUndo, setSkillUndo] = useState(null);
    const [personalityUndo, setPersonalityUndo] = useState(null);
    const onEnhanceSkill = useCallback(async () => {
        const draft = skill.trim();
        if (!draft || busy)
            return;
        setBusy('enhance');
        setErr(null);
        try {
            const r = await api.post(`/p/${projectId}/coworkers/enhance-skill`, {
                draft,
                ...(name.trim() ? { name: name.trim() } : {}),
            });
            if (r.skill) {
                setSkillUndo(skill);
                setSkill(r.skill);
            }
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'enhance failed');
        }
        finally {
            setBusy(null);
        }
    }, [projectId, skill, name, busy]);
    const onRandomizePersonality = useCallback(async () => {
        if (busy)
            return;
        setBusy('randomize');
        setErr(null);
        try {
            const r = await api.post(`/p/${projectId}/coworkers/randomize-personality`, {
                ...(name.trim() ? { name: name.trim() } : {}),
            });
            if (r.personality) {
                setPersonalityUndo(personality);
                setPersonality(r.personality);
            }
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'randomize failed');
        }
        finally {
            setBusy(null);
        }
    }, [projectId, personality, name, busy]);
    const onSave = useCallback(async () => {
        if (busy)
            return;
        if (!name.trim()) {
            setErr('Name is required');
            return;
        }
        if (!skill.trim()) {
            setErr('Skill is required');
            return;
        }
        setBusy('save');
        setErr(null);
        // participants (I19): personal=true sends [viewerId]; personal=false
        // sends null (server interprets as "project-wide"). The server
        // defensively re-includes the editor's id even if absent, so even
        // if a future "share with X people" flow forgets the editor we
        // don't accidentally lock them out.
        const participants = personal && user?.userId ? [user.userId] : null;
        const body = {
            name: name.trim(),
            skill: skill.trim(),
            personality: personality.trim(),
            avatarEmoji: avatarEmoji.trim() || '\uD83E\uDD16',
            participants,
        };
        try {
            if (existing) {
                await api.patch(`/p/${projectId}/coworkers/${existing.coworkerId}`, body);
            }
            else {
                await api.post(`/p/${projectId}/coworkers`, body);
            }
            await onSaved();
        }
        catch (e) {
            setErr(e instanceof Error ? e.message : 'save failed');
            setBusy(null);
        }
    }, [projectId, existing, name, skill, personality, avatarEmoji, personal, user?.userId, busy, onSaved]);
    return (_jsx("div", { className: "modal-bg", onClick: () => !busy && onClose(), children: _jsxs("div", { className: "modal coworker-edit-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { style: { marginTop: 0 }, children: existing ? 'Edit co-worker' : 'New co-worker' }), _jsxs("div", { className: "coworker-field", children: [_jsx("label", { children: "Avatar & name" }), _jsxs("div", { style: { display: 'flex', gap: 8, alignItems: 'center' }, children: [_jsx(AvatarPicker, { value: avatarEmoji, onChange: setAvatarEmoji, title: "Choose avatar emoji" }), _jsx("input", { autoFocus: true, style: { flex: 1 }, value: name, onChange: (e) => setName(e.target.value), placeholder: "e.g. Marina the Financial Analyst", maxLength: 80 })] })] }), _jsxs("div", { className: "coworker-field", children: [_jsxs("div", { className: "coworker-field-head", children: [_jsx("label", { children: "Superpower (Skill)" }), _jsxs("div", { className: "coworker-field-actions", children: [skillUndo !== null && (_jsx("button", { type: "button", className: "ghost", onClick: () => { setSkill(skillUndo); setSkillUndo(null); }, title: "Restore your previous draft", children: '\u21B6 Undo' })), _jsx("button", { type: "button", disabled: !skill.trim() || busy === 'enhance', onClick: () => void onEnhanceSkill(), title: "Use AI to expand your draft into a polished skill description", children: busy === 'enhance' ? 'Enhancing\u2026' : '\u2728 Enhance' })] })] }), _jsx("textarea", { value: skill, onChange: (e) => setSkill(e.target.value), placeholder: "Describe what this agent should be really good at. The Enhance button will polish a rough draft and reference the FDN tools the agent has available.", rows: 8, maxLength: 8000 }), _jsxs("div", { className: "coworker-field-hint", children: [skill.length, " / 8000 chars"] })] }), _jsxs("div", { className: "coworker-field", children: [_jsxs("div", { className: "coworker-field-head", children: [_jsx("label", { children: "Personality (optional)" }), _jsxs("div", { className: "coworker-field-actions", children: [personalityUndo !== null && (_jsx("button", { type: "button", className: "ghost", onClick: () => { setPersonality(personalityUndo); setPersonalityUndo(null); }, title: "Restore your previous draft", children: '\u21B6 Undo' })), _jsx("button", { type: "button", disabled: busy === 'randomize', onClick: () => void onRandomizePersonality(), title: "Generate a creative personality (Michael Scott / Ted from Bill & Ted / etc). Uses the name as a hint.", children: busy === 'randomize' ? 'Rolling\u2026' : '\uD83C\uDFB2 Randomize' })] })] }), _jsx("textarea", { value: personality, onChange: (e) => setPersonality(e.target.value), placeholder: "Optional. Voice, attitude, quirks. e.g. perpetually mid-sigh; complains before each task; impeccable underlying work.", rows: 5, maxLength: 2000 }), _jsxs("div", { className: "coworker-field-hint", children: [personality.length, " / 2000 chars"] })] }), _jsx("div", { className: "coworker-field", children: _jsxs("label", { style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            cursor: 'pointer',
                            userSelect: 'none',
                        }, children: [_jsx("input", { type: "checkbox", checked: personal, onChange: (e) => setPersonal(e.target.checked), disabled: !!busy, style: { width: 16, height: 16 } }), _jsxs("span", { style: { display: 'flex', flexDirection: 'column' }, children: [_jsxs("span", { style: { fontWeight: 500 }, children: ['\uD83D\uDD12', " Personal co-worker"] }), _jsx("span", { style: { fontSize: 12, color: 'var(--fg-muted)' }, children: personal
                                            ? 'Only you can see and chat with this co-worker.'
                                            : 'Visible to everyone in the project.' })] })] }) }), err && _jsx("div", { className: "error", children: err }), _jsxs("div", { style: { display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }, children: [_jsx("button", { type: "button", className: "ghost", disabled: !!busy, onClick: onClose, children: "Cancel" }), _jsx("button", { type: "button", disabled: !!busy || !name.trim() || !skill.trim(), onClick: () => void onSave(), children: busy === 'save' ? 'Saving\u2026' : (existing ? 'Save' : 'Create') })] })] }) }));
}
